import type { KeyResult, Objective, Task } from '@portfolio-pm/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { ProgressBar, StatusBadge } from './okr-ui.tsx';

interface OkrTimelineProps {
  rootObjectives: Objective[];
  childrenByParent: Map<string, Objective[]>;
  keyResultsByObjective: Map<string, KeyResult[]>;
  tasksByKrId: Map<string, Task[]>;
  getObjectiveProgress: (id: string) => number;
  getKrProgress: (id: string) => number;
}

function getQuarterRange(date: Date): { start: Date; end: Date; label: string } {
  const q = Math.floor(date.getMonth() / 3);
  const year = date.getFullYear();
  const start = new Date(Date.UTC(year, q * 3, 1));
  const end = new Date(Date.UTC(year, q * 3 + 3, 0));
  const qLabel = ['Q1', 'Q2', 'Q3', 'Q4'][q];
  return { start, end, label: `${qLabel} ${year}` };
}

function buildWeeks(start: Date, end: Date): Date[] {
  const weeks: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    weeks.push(new Date(cur));
    cur.setUTCDate(cur.getUTCDate() + 7);
  }
  return weeks.slice(0, 13);
}

function pctInRange(ts: number, start: Date, end: Date): number {
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, ((ts - start.getTime()) / total) * 100));
}

function getKrBarColor(progress: number): string {
  if (progress >= 70) {
    return 'bg-[color:var(--app-success-soft)] border-[color:var(--app-success)] text-[color:var(--app-success)]';
  }
  if (progress >= 40) {
    return 'bg-[color:var(--app-warning-soft)] border-[color:var(--app-warning)] text-[color:var(--app-warning)]';
  }
  return 'bg-[color:var(--app-danger-soft)] border-[color:var(--app-danger)] text-[color:var(--app-danger)]';
}

export function OkrTimeline({
  rootObjectives,
  childrenByParent,
  keyResultsByObjective,
  tasksByKrId,
  getObjectiveProgress,
  getKrProgress,
}: OkrTimelineProps): JSX.Element {
  const today = new Date();
  const { start: qStart, end: qEnd, label: qLabel } = getQuarterRange(today);
  const weeks = buildWeeks(qStart, qEnd);
  const todayPct = pctInRange(today.getTime(), qStart, qEnd);

  const [expandedKrIds, setExpandedKrIds] = useState<Set<string>>(new Set());
  const [collapsedObjIds, setCollapsedObjIds] = useState<Set<string>>(new Set());

  const toggleKr = (id: string): void => {
    setExpandedKrIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleObj = (id: string): void => {
    setCollapsedObjIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getBarPosition = (obj: Objective): { left: string; width: string } => {
    const start = obj.startDate ?? qStart.getTime();
    const end = obj.endDate ?? qEnd.getTime();
    const leftPct = pctInRange(start, qStart, qEnd);
    const rightPct = pctInRange(end, qStart, qEnd);
    return {
      left: `${leftPct}%`,
      width: `${Math.max(4, rightPct - leftPct)}%`,
    };
  };

  const trackStyle = {
    backgroundImage:
      'repeating-linear-gradient(90deg, transparent 0, transparent calc(100%/13 - 1px), rgba(210, 214, 209, 0.7) calc(100%/13 - 1px), rgba(210, 214, 209, 0.7) calc(100%/13))',
    backgroundSize: '100% 100%',
  };

  const renderRows = (
    objective: Objective,
    depth = 0,
    visited = new Set<string>(),
  ): JSX.Element[] => {
    if (visited.has(objective.id)) return [];
    const nextVisited = new Set(visited);
    nextVisited.add(objective.id);

    const progress = getObjectiveProgress(objective.id);
    const keyResults = keyResultsByObjective.get(objective.id) ?? [];
    const children = (childrenByParent.get(objective.id) ?? []).filter(
      (c) => !nextVisited.has(c.id),
    );
    const isCollapsed = collapsedObjIds.has(objective.id);
    const barPos = getBarPosition(objective);

    const statusBarClass =
      objective.status === 'on_track'
        ? 'bg-[color:var(--app-success-soft)] border-[color:var(--app-success)] text-[color:var(--app-success)]'
        : objective.status === 'at_risk'
          ? 'bg-[color:var(--app-warning-soft)] border-[color:var(--app-warning)] text-[color:var(--app-warning)]'
          : 'bg-[color:var(--app-danger-soft)] border-[color:var(--app-danger)] text-[color:var(--app-danger)]';

    const rows: JSX.Element[] = [];

    // Objective group header
    rows.push(
      <div
        key={`obj-${objective.id}`}
        className="grid border-b border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)]"
        style={{ gridTemplateColumns: '240px 1fr' }}
      >
        <div
          className="flex items-center gap-1.5 py-2"
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          <button
            type="button"
            onClick={() => toggleObj(objective.id)}
            className="flex h-4 w-4 shrink-0 items-center justify-center text-[color:var(--app-text-soft)] hover:text-[color:var(--app-text)]"
          >
            {isCollapsed ? (
              <ChevronRight aria-hidden="true" size={13} />
            ) : (
              <ChevronDown aria-hidden="true" size={13} />
            )}
          </button>
          <span className="truncate text-xs font-semibold text-[color:var(--app-text)]">
            {objective.title}
          </span>
          <StatusBadge status={objective.status} />
          <span className="ml-auto shrink-0 font-mono text-[10px] text-[color:var(--app-text-soft)]">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="relative flex items-center" style={trackStyle}>
          {/* Today marker */}
          <div
            className="absolute top-0 bottom-0 z-10 w-px bg-[color:var(--app-warning)] opacity-80"
            style={{ left: `${todayPct}%` }}
          />
          <div
            className={`absolute flex h-5 items-center rounded border px-2 text-[10px] font-medium shadow-sm ${statusBarClass}`}
            style={barPos}
          >
            {qLabel} · {Math.round(progress)}%
          </div>
        </div>
      </div>,
    );

    if (!isCollapsed) {
      // KR rows
      for (const kr of keyResults) {
        const krProgress = getKrProgress(kr.id);
        const isKrExpanded = expandedKrIds.has(kr.id);
        const linkedTasks = tasksByKrId.get(kr.id) ?? [];
        const doneCount = linkedTasks.filter((t) => t.completedAt).length;
        const barClass = getKrBarColor(krProgress);

        rows.push(
          <div
            key={`kr-${kr.id}`}
            className="grid border-b border-dashed border-[color:var(--app-border)]"
            style={{ gridTemplateColumns: '240px 1fr', minHeight: '32px' }}
          >
            <div
              className="flex cursor-pointer items-center gap-1.5 py-1.5 hover:bg-white/55"
              style={{ paddingLeft: `${28 + depth * 16}px` }}
              onClick={() => {
                if (linkedTasks.length > 0) toggleKr(kr.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && linkedTasks.length > 0) toggleKr(kr.id);
              }}
            >
              {linkedTasks.length > 0 ? (
                <span className="flex w-3 shrink-0 items-center justify-center text-[color:var(--app-text-soft)]">
                  {isKrExpanded ? (
                    <ChevronDown aria-hidden="true" size={12} />
                  ) : (
                    <ChevronRight aria-hidden="true" size={12} />
                  )}
                </span>
              ) : (
                <span className="w-3 shrink-0" />
              )}
              <span className="shrink-0 font-mono text-[10px] uppercase text-[color:var(--app-text-soft)]">
                KR
              </span>
              <span className="flex-1 truncate text-xs text-[color:var(--app-text-muted)]">
                {kr.title}
              </span>
              {linkedTasks.length > 0 && (
                <span className="shrink-0 font-mono text-[10px] text-[color:var(--app-text-soft)]">
                  {doneCount}/{linkedTasks.length}
                </span>
              )}
              <div className="w-12 shrink-0 pr-2">
                <ProgressBar value={krProgress} colorClass="bg-[color:var(--app-info)]" />
              </div>
            </div>
            <div className="relative flex items-center" style={trackStyle}>
              <div
                className="absolute top-0 bottom-0 z-10 w-px bg-[color:var(--app-warning)] opacity-80"
                style={{ left: `${todayPct}%` }}
              />
              <div
                className={`absolute flex h-4 items-center rounded border px-1.5 text-[9px] font-medium ${barClass}`}
                style={barPos}
              >
                {Math.round(krProgress)}%
              </div>
            </div>
          </div>,
        );

        // Task rows when KR is expanded
        if (isKrExpanded) {
          for (const task of linkedTasks) {
            rows.push(
              <div
                key={`task-${task.id}`}
                className="grid border-b border-dashed border-[color:var(--app-border)] bg-white/45"
                style={{ gridTemplateColumns: '240px 1fr', minHeight: '26px' }}
              >
                <div
                  className="flex items-center gap-2 py-1"
                  style={{ paddingLeft: `${48 + depth * 16}px` }}
                >
                  <input
                    type="checkbox"
                    checked={!!task.completedAt}
                    readOnly
                    className="shrink-0 accent-[color:var(--app-info)]"
                  />
                  <span
                    className={`text-xs ${
                      task.completedAt
                        ? 'text-[color:var(--app-text-soft)] line-through'
                        : 'text-[color:var(--app-text-muted)]'
                    }`}
                  >
                    {task.title}
                  </span>
                </div>
                <div className="relative" style={trackStyle}>
                  <div
                    className="absolute top-0 bottom-0 z-10 w-px bg-[color:var(--app-warning)] opacity-80"
                    style={{ left: `${todayPct}%` }}
                  />
                </div>
              </div>,
            );
          }
        }
      }

      // Child objectives (sub-objectives)
      for (const child of children) {
        rows.push(...renderRows(child, depth + 1, nextVisited));
      }
    }

    return rows;
  };

  if (rootObjectives.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[color:var(--app-text-muted)]">
        No objectives to display on the timeline.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Timeline header */}
      <div
        className="grid shrink-0 border-b border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] text-[10px] font-semibold text-[color:var(--app-text-muted)]"
        style={{ gridTemplateColumns: '240px 1fr' }}
      >
        <div className="px-3 py-2">Objective / KR</div>
        <div className="grid py-2" style={{ gridTemplateColumns: `repeat(${weeks.length}, 1fr)` }}>
          {weeks.map((w) => (
            <div
              key={w.getTime()}
              className="truncate border-l border-[color:var(--app-border)] px-1 text-center"
            >
              {w.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable row area */}
      <div className="min-h-0 flex-1 overflow-auto">
        {rootObjectives.map((obj) => renderRows(obj))}
      </div>

      {/* Footer strip */}
      <div className="shrink-0 border-t border-dashed border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] px-4 py-2.5 text-xs text-[color:var(--app-text-muted)]">
        <span className="inline-flex items-center gap-2">
          <span
            className="inline-block h-3 w-px bg-[color:var(--app-warning)]"
            style={{ verticalAlign: 'middle' }}
          />
          Today ·{' '}
          {today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          <span className="text-[color:var(--app-text-soft)]">·</span>
          {qLabel} timeline · click KRs with tasks to expand
        </span>
      </div>
    </div>
  );
}
